import React, { useState, useEffect } from 'react';
import { getToken } from '../utils/storage';
import SuperAdminLayout from '../components/SuperAdminLayout';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowUpDown, ArrowUp, ArrowDown, ChevronLeft, ChevronRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SuperAdminLoginHistory = () => {
  const { t } = useTranslation();
  const [history, setHistory] = useState([]);
  const [limit, setLimit] = useState(500); // Fetch more data for pagination
  const [sortField, setSortField] = useState('login_at');
  const [sortDirection, setSortDirection] = useState('desc'); // desc = newest first
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(25);

  useEffect(() => {
    fetchHistory();
  }, [limit]);

  const fetchHistory = async () => {
    try {
      const token = getToken();
      const response = await axios.get(`${API}/admin/login-history?limit=${limit}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setHistory(response.data);
    } catch (error) {
      toast.error('Kunne ikke laste innloggingshistorikk');
    }
  };

  const handleSort = (field) => {
    if (sortField === field) {
      // Toggle direction if same field
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // New field, start with ascending
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const getSortedHistory = () => {
    const sorted = [...history].sort((a, b) => {
      let aVal = a[sortField];
      let bVal = b[sortField];

      // Handle special cases
      if (sortField === 'login_at') {
        aVal = new Date(aVal).getTime();
        bVal = new Date(bVal).getTime();
      } else if (sortField === 'role') {
        // Sort super_admin before customer_admin
        aVal = aVal === 'super_admin' ? 0 : 1;
        bVal = bVal === 'super_admin' ? 0 : 1;
      } else if (sortField === 'organization_name') {
        // Handle null/undefined, put '-' last
        aVal = aVal === '-' || !aVal ? 'zzz' : aVal.toLowerCase();
        bVal = bVal === '-' || !bVal ? 'zzz' : bVal.toLowerCase();
      } else {
        // String comparison
        aVal = (aVal || '').toLowerCase();
        bVal = (bVal || '').toLowerCase();
      }

      if (sortDirection === 'asc') {
        return aVal > bVal ? 1 : -1;
      } else {
        return aVal < bVal ? 1 : -1;
      }
    });

    return sorted;
  };

  const SortIcon = ({ field }) => {
    if (sortField !== field) {
      return <ArrowUpDown size={14} style={{ opacity: 0.3 }} />;
    }
    return sortDirection === 'asc' ? 
      <ArrowUp size={14} color="#50E3C2" /> : 
      <ArrowDown size={14} color="#50E3C2" />;
  };

  // Pagination logic
  const sortedHistory = getSortedHistory();
  const totalPages = Math.ceil(sortedHistory.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentItems = sortedHistory.slice(startIndex, endIndex);

  const goToPage = (page) => {
    setCurrentPage(Math.max(1, Math.min(page, totalPages)));
  };

  const handleItemsPerPageChange = (newItemsPerPage) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1); // Reset to first page when changing items per page
  };

  return (
    <SuperAdminLayout>
      <div className="admin-header">
        <h1>{t('superAdmin.loginHistory.title')}</h1>
        <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
          <span style={{ color: '#9ca3af', fontSize: '0.9rem' }}>{t('common.loading')}</span>
          <select
            value={itemsPerPage}
            onChange={(e) => handleItemsPerPageChange(parseInt(e.target.value))}
            style={{
              padding: '0.5rem 1rem',
              borderRadius: '8px',
              background: '#1a1f2e',
              color: 'white',
              border: '1px solid rgba(255,255,255,0.1)'
            }}
          >
            <option value="25">25</option>
            <option value="50">50</option>
            <option value="100">100</option>
          </select>
        </div>
      </div>

      <div className="admin-table">
        <table>
          <thead>
            <tr>
              <th 
                onClick={() => handleSort('email')} 
                style={{ cursor: 'pointer', userSelect: 'none' }}
                title={t('common.filter')}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  {t('superAdmin.loginHistory.user')}
                  <SortIcon field="email" />
                </div>
              </th>
              <th 
                onClick={() => handleSort('role')} 
                style={{ cursor: 'pointer', userSelect: 'none' }}
                title={t('common.filter')}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  {t('superAdmin.users.role')}
                  <SortIcon field="role" />
                </div>
              </th>
              <th 
                onClick={() => handleSort('organization_name')} 
                style={{ cursor: 'pointer', userSelect: 'none' }}
                title={t('common.filter')}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  {t('superAdmin.loginHistory.organization')}
                  <SortIcon field="organization_name" />
                </div>
              </th>
              <th 
                onClick={() => handleSort('login_at')} 
                style={{ cursor: 'pointer', userSelect: 'none' }}
                title={t('common.filter')}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  {t('superAdmin.loginHistory.timestamp')}
                  <SortIcon field="login_at" />
                </div>
              </th>
              <th 
                onClick={() => handleSort('ip_address')} 
                style={{ cursor: 'pointer', userSelect: 'none' }}
                title={t('common.filter')}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  {t('superAdmin.loginHistory.ipAddress')}
                  <SortIcon field="ip_address" />
                </div>
              </th>
            </tr>
          </thead>
          <tbody>
            {currentItems.map((entry, idx) => (
              <tr key={idx}>
                <td>{entry.email}</td>
                <td>
                  <span className={entry.role === 'super_admin' ? 'badge badge-danger' : 'badge badge-primary'}>
                    {entry.role === 'super_admin' ? 'Super Admin' : 'Kunde Admin'}
                  </span>
                </td>
                <td>{entry.organization_name || '-'}</td>
                <td>{new Date(entry.login_at).toLocaleString('no-NO')}</td>
                <td><code>{entry.ip_address || '-'}</code></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination Controls */}
      {totalPages > 1 && (
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginTop: '1.5rem',
          padding: '1rem',
          background: 'rgba(26, 31, 46, 0.5)',
          borderRadius: '8px',
          border: '1px solid rgba(255, 255, 255, 0.1)'
        }}>
          {/* Page info */}
          <div style={{ color: '#9ca3af', fontSize: '0.9rem' }}>
            {t('common.loading')} {startIndex + 1}-{Math.min(endIndex, sortedHistory.length)} {t('common.loading')} {sortedHistory.length}
          </div>

          {/* Pagination buttons */}
          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
            {/* Previous button */}
            <button
              onClick={() => goToPage(currentPage - 1)}
              disabled={currentPage === 1}
              style={{
                padding: '0.5rem 0.75rem',
                background: currentPage === 1 ? 'rgba(255,255,255,0.05)' : 'rgba(80, 227, 194, 0.1)',
                border: '1px solid rgba(255,255,255,0.1)',
                borderRadius: '6px',
                color: currentPage === 1 ? '#6b7280' : '#50E3C2',
                cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '0.25rem'
              }}
            >
              <ChevronLeft size={16} />
              {t('common.previous')}
            </button>

            {/* Page numbers */}
            <div style={{ display: 'flex', gap: '0.25rem' }}>
              {[...Array(totalPages)].map((_, i) => {
                const pageNum = i + 1;
                // Show first page, last page, current page, and pages around current
                if (
                  pageNum === 1 ||
                  pageNum === totalPages ||
                  (pageNum >= currentPage - 1 && pageNum <= currentPage + 1)
                ) {
                  return (
                    <button
                      key={pageNum}
                      onClick={() => goToPage(pageNum)}
                      style={{
                        padding: '0.5rem 0.75rem',
                        background: pageNum === currentPage ? '#50E3C2' : 'rgba(255,255,255,0.05)',
                        border: '1px solid rgba(255,255,255,0.1)',
                        borderRadius: '6px',
                        color: pageNum === currentPage ? '#1a1f2e' : '#ffffff',
                        cursor: 'pointer',
                        fontWeight: pageNum === currentPage ? '600' : '400',
                        minWidth: '40px'
                      }}
                    >
                      {pageNum}
                    </button>
                  );
                } else if (
                  pageNum === currentPage - 2 ||
                  pageNum === currentPage + 2
                ) {
                  return (
                    <span key={pageNum} style={{ padding: '0.5rem', color: '#6b7280' }}>
                      ...
                    </span>
                  );
                }
                return null;
              })}
            </div>

            {/* Next button */}
            <button
              onClick={() => goToPage(currentPage + 1)}
              disabled={currentPage === totalPages}
              style={{
                padding: '0.5rem 0.75rem',
                background: currentPage === totalPages ? 'rgba(255,255,255,0.05)' : 'rgba(80, 227, 194, 0.1)',
                border: '1px solid rgba(255,255,255,0.1)',
                borderRadius: '6px',
                color: currentPage === totalPages ? '#6b7280' : '#50E3C2',
                cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '0.25rem'
              }}
            >
              {t('common.next')}
              <ChevronRight size={16} />
            </button>
          </div>
        </div>
      )}
    </SuperAdminLayout>
  );
};

export default SuperAdminLoginHistory;
